import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../auth/session';
import { DeviceVerification } from './DeviceVerification';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { Alert } from '../ui';
import { AuthPage, Eye, Label } from './auth-parts';

/** P-01 — archetype 1. */
export function SignIn({ onApply, onForgot }: { onApply: () => void; onForgot: () => void }) {
  const { signIn, refreshMe } = useSession();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the server asks for a device code instead of opening a session (P-07). */
  const [challenge, setChallenge] = useState<{ sentTo: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Wording only: the form is `noValidate`, so say what is missing before the round trip.
    const trimmed = email.trim();
    if (!trimmed) { setError(t('auth.emailRequired')); return; }
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) { setError(t('auth.emailInvalid')); return; }
    if (!password) { setError(t('auth.passwordRequired')); return; }
    setBusy(true);
    setError(null);
    try {
      const pending = await signIn(email, password);
      // The password was right, the browser is not trusted. Hand over to P-07 and do
      // NOT navigate: there is no session yet.
      if (pending) { setChallenge(pending); return; }
      /**
       * Land on the front door, whatever page the session ended on.
       *
       * `SignIn` renders in PLACE of the routes rather than at a route of its
       * own, so the address bar never changed: signing out on a product and back
       * in returned you to that product. Which is the wrong answer even when the
       * page is still permitted — a new session starts at the beginning — and a
       * broken one when it is not, because a role can change between sessions.
       *
       * `/` and not `/dashboard`: the redirect at `/` already knows where each
       * kind of user belongs, and a retailer with no dashboard must not be sent
       * to one.
       */
      void navigate('/', { replace: true });
    } catch (err) {
      // The server says "Those credentials were not recognised" and does not
      // distinguish a wrong password from an unknown address, deliberately —
      // the difference tells an attacker which addresses exist.
      setError(err instanceof ApiError ? err.message : t('auth.signInFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <DeviceVerification
        sentTo={challenge.sentTo}
        onVerified={() => { void refreshMe().then(() => navigate('/', { replace: true })); }}
        onCancel={() => { setChallenge(null); setPassword(''); }}
      />
    );
  }

  return (
    <AuthPage eyebrow={t('auth.eyebrow')} title={t('auth.signIn')}>
      <form onSubmit={(e) => void submit(e)} noValidate>
        {error ? <Alert tone="stop">{error}</Alert> : null}
        <div className="mk-auth-field">
          <div className="mk-auth-labelrow">
            <Label htmlFor="email" required>{t('auth.email')}</Label>
          </div>
          <input
            id="email" type="email" required autoComplete="username"
            placeholder={t('auth.emailPlaceholder')}
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="mk-auth-field">
          <div className="mk-auth-labelrow">
            <Label htmlFor="password" required>{t('auth.password')}</Label>
            <button type="button" className="mk-auth-link" onClick={onForgot}>
              {t('auth.forgot')}
            </button>
          </div>
          <div className="mk-auth-wrap">
            <input
              id="password" type={reveal ? 'text' : 'password'} required
              autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button" className="mk-auth-reveal"
              aria-label={reveal ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-pressed={reveal}
              onClick={() => setReveal(!reveal)}
            >
              <Eye off={reveal} />
            </button>
          </div>
        </div>

        <button className="mk-auth-submit" type="submit" disabled={busy}>
          {busy ? t('auth.submitting') : t('auth.submit')}
        </button>
      </form>

      <hr className="mk-auth-rule" />
      <div className="mk-auth-foot">
        <p>{t('auth.newHere')}</p>
        <button type="button" className="mk-auth-alt" onClick={onApply}>
          {t('auth.applyCta')}
        </button>
      </div>
    </AuthPage>
  );
}
